import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Redirect} from "react-router-dom";
import * as keys from 'keys'
import {apiPost} from "../services/api-call-helpers";
import {globalAuthManager} from "../services/GlobalServices";
import {AppContext, InvestmentAppContext} from "./contexts";

function hasTestingPanelAccess(username) {
    return typeof username === 'string' && username.trim().toLowerCase().endsWith('@clarivor.com')
}

export default function TestingPanel(props) {
    const appContext = useContext(AppContext)
    const investmentAppContext = useContext(InvestmentAppContext)
    const username = appContext?.username
    const investmentAccounts = investmentAppContext?.investmentAccounts || []
    const sortedAccounts = useMemo(
        () => investmentAccounts.slice().sort((a, b) => a.name.localeCompare(b.name)),
        [investmentAccounts]
    )
    const [selectedAccountId, setSelectedAccountId] = useState(
        sortedAccounts.length > 0 ? String(sortedAccounts[0].id) : ''
    )
    const [submitting, setSubmitting] = useState(false)
    const [result, setResult] = useState(null)
    const [errorMessage, setErrorMessage] = useState(null)

    useEffect(() => {
        if (sortedAccounts.length > 0 && !selectedAccountId) {
            setSelectedAccountId(String(sortedAccounts[0].id))
        }
    }, [sortedAccounts, selectedAccountId])

    if (!username) {
        return null
    }

    if (!hasTestingPanelAccess(username)) {
        return <Redirect to='/app'/>
    }

    const runTradeAuthCheck = async () => {
        if (!selectedAccountId) {
            setErrorMessage('Select an investment account first.')
            setResult(null)
            return
        }

        setSubmitting(true)
        setErrorMessage(null)
        setResult(null)

        try {
            const response = await apiPost(
                keys.API_ENDPOINT.CHECK_IB_TRADE_AUTH,
                {investment_account: selectedAccountId},
                globalAuthManager
            )
            setResult(response.data)
        } catch (error) {
            const statusCode = error?.response?.status
            if (statusCode === 403) {
                setErrorMessage('Testing is not available for this user.')
            } else {
                setErrorMessage(error?.response?.data?.message || `Request failed${statusCode ? ` (${statusCode})` : ''}.`)
            }
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className='container py-4'>
            <div className='row justify-content-center'>
                <div className='col-12 col-lg-8'>
                    <div className='card'>
                        <div className='card-body'>
                            <h3 className='mb-3'>Internal Testing</h3>
                            <p className='text-muted mb-4'>
                                This page does not place orders. The only action available here is a server-side
                                IB trade-auth check through the portal backend.
                            </p>

                            <div className='form-group'>
                                <label htmlFor='testing-investment-account'>Investment Account</label>
                                <select
                                    id='testing-investment-account'
                                    className='form-control'
                                    value={selectedAccountId}
                                    onChange={(event) => setSelectedAccountId(event.target.value)}
                                >
                                    {
                                        sortedAccounts.length === 0 ?
                                            <option value=''>No investment accounts available</option> :
                                            sortedAccounts.map(account => (
                                                <option key={account.id} value={account.id}>
                                                    {account.name}
                                                </option>
                                            ))
                                    }
                                </select>
                            </div>

                            <button
                                className='btn btn-outline-secondary'
                                onClick={runTradeAuthCheck}
                                disabled={submitting || !selectedAccountId}
                            >
                                {submitting ? 'Checking...' : 'Check IB Trade Auth'}
                            </button>

                            {
                                errorMessage ?
                                    <div className='alert alert-danger mt-3 mb-0'>{errorMessage}</div> :
                                    null
                            }

                            {
                                result ?
                                    <div className='alert alert-success mt-3 mb-0'>
                                        <div>Auth check succeeded.</div>
                                        <div className='small mt-2'>Broker: {result.broker}</div>
                                        <div className='small'>Account mode: {result.account_mode || 'unknown'}</div>
                                        <div className='small'>Investment account: {result.investment_account}</div>
                                    </div> :
                                    null
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
